import type {
  DecisionLedgerDecisionDto,
  DecisionLedgerPortfolioPackageDto,
  DecisionLedgerRealizationCommitmentDto,
  DecisionLedgerVerificationPlanMetricDto,
} from './decision-ledger';

export type DecisionLedgerReceiptField =
  | 'ledgerId'
  | 'actor'
  | 'timestamp'
  | 'rationale'
  | 'acceptedAssumptions'
  | 'driftThresholds'
  | 'reviewDate';

export type DecisionLedgerReceiptAbsentField = {
  field: DecisionLedgerReceiptField;
  reason: string;
};

export type DecisionLedgerReceiptDriftThresholdPresentation = {
  metricKey: DecisionLedgerVerificationPlanMetricDto['metricKey'];
  label: DecisionLedgerVerificationPlanMetricDto['label'];
  unit: DecisionLedgerVerificationPlanMetricDto['unit'];
  warningThresholdRatio: number | null;
  driftThresholdRatio: number | null;
  amendmentThresholdRatio: number | null;
  targetAt: DecisionLedgerVerificationPlanMetricDto['targetAt'];
};

export type DecisionLedgerReceiptPresentation = {
  ledgerId?: string;
  actor?: string;
  timestamp?: string;
  rationale?: string;
  acceptedAssumptions?: string[];
  driftThresholds?: DecisionLedgerReceiptDriftThresholdPresentation[];
  reviewDate?: string;
  absentFields: DecisionLedgerReceiptAbsentField[];
};

export type DecisionLedgerReceiptDecisionSource = Pick<
  DecisionLedgerDecisionDto,
  'decisionId' | 'lifecycle' | 'verificationPlan'
>;

export type DecisionLedgerReceiptCommitmentSource = Pick<
  DecisionLedgerRealizationCommitmentDto,
  'decisionId' | 'gateRef' | 'proposalMetadata'
>;

export type DecisionLedgerReceiptPortfolioPackageSource = Pick<
  DecisionLedgerPortfolioPackageDto,
  'recommendation' | 'tradeoffSummary' | 'governance' | 'commitmentMemory' | 'controlPlan'
>;

export type DecisionLedgerReceiptSource = {
  decision?: DecisionLedgerReceiptDecisionSource | null;
  commitment?: DecisionLedgerReceiptCommitmentSource | null;
  portfolioPackage?: DecisionLedgerReceiptPortfolioPackageSource | null;
  rationale?: string | null;
  acceptedAssumptions?: string[] | null;
  reviewDate?: string | null;
};

const fieldOrder: DecisionLedgerReceiptField[] = [
  'ledgerId',
  'actor',
  'timestamp',
  'rationale',
  'acceptedAssumptions',
  'driftThresholds',
  'reviewDate',
];

const absentReasons: Record<DecisionLedgerReceiptField, string> = {
  ledgerId: 'No decisionId or commitment ledger id is available.',
  actor: 'No activatedByUserId, actorUserId, or approverUserIds value is available.',
  timestamp: 'No activatedAt, createdAt, or decisionCreatedAt timestamp is available.',
  rationale: 'No explicit rationale, recommendation, tradeoffSummary, or intentText is available.',
  acceptedAssumptions: 'No acceptedAssumptions source field is available.',
  driftThresholds: 'No verificationPlan metric thresholds are available.',
  reviewDate: 'No reviewDate or nextReviewDueAt value is available.',
};

export function mapDecisionLedgerReceiptPresentation(
  source: DecisionLedgerReceiptSource,
): DecisionLedgerReceiptPresentation {
  const ledgerId = firstNonEmpty(
    source.decision?.decisionId,
    source.commitment?.gateRef.decisionLedgerDecisionId,
    source.commitment?.decisionId,
    source.portfolioPackage?.commitmentMemory?.commitmentPackageId,
  );
  const actor = firstNonEmpty(
    source.commitment?.gateRef.activatedByUserId,
    source.decision?.lifecycle?.actorUserId,
    formatActorList(source.portfolioPackage?.governance.approverUserIds),
  );
  const timestamp = firstNonEmpty(
    source.commitment?.gateRef.activatedAt,
    source.decision?.lifecycle?.createdAt,
    source.portfolioPackage?.governance.decisionCreatedAt,
  );
  const rationale = firstNonEmpty(
    source.rationale,
    source.portfolioPackage?.recommendation,
    source.portfolioPackage?.tradeoffSummary,
    source.commitment?.proposalMetadata?.intentText,
  );
  const acceptedAssumptions = nonEmptyList(source.acceptedAssumptions);
  const driftThresholds = mapDriftThresholds(source.decision?.verificationPlan?.metrics);
  const reviewDate = firstNonEmpty(
    source.reviewDate,
    source.portfolioPackage?.governance.nextReviewDueAt,
    source.portfolioPackage?.commitmentMemory?.nextReviewDueAt,
    source.portfolioPackage?.controlPlan?.nextReviewDueAt,
  );

  const receipt: Omit<DecisionLedgerReceiptPresentation, 'absentFields'> = {
    ledgerId,
    actor,
    timestamp,
    rationale,
    acceptedAssumptions,
    driftThresholds,
    reviewDate,
  };

  return {
    ...receipt,
    absentFields: fieldOrder
      .filter((field) => isReceiptFieldAbsent(field, receipt))
      .map((field) => ({ field, reason: absentReasons[field] })),
  };
}

function firstNonEmpty(...values: Array<string | null | undefined>): string | undefined {
  for (const value of values) {
    const trimmed = value?.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

function nonEmptyList(values: string[] | null | undefined): string[] | undefined {
  const cleaned = values?.map((value) => value.trim()).filter(Boolean);
  return cleaned && cleaned.length > 0 ? cleaned : undefined;
}

function formatActorList(values: string[] | null | undefined): string | undefined {
  const actors = nonEmptyList(values);
  return actors ? actors.join(' + ') : undefined;
}

function mapDriftThresholds(
  metrics: DecisionLedgerVerificationPlanMetricDto[] | null | undefined,
): DecisionLedgerReceiptDriftThresholdPresentation[] | undefined {
  if (!metrics || metrics.length === 0) return undefined;

  const thresholds = metrics
    .filter(
      (metric) =>
        metric.warningThresholdRatio !== undefined ||
        metric.driftThresholdRatio !== undefined ||
        metric.amendmentThresholdRatio !== undefined,
    )
    .map((metric) => ({
      metricKey: metric.metricKey,
      label: metric.label,
      unit: metric.unit,
      warningThresholdRatio: metric.warningThresholdRatio ?? null,
      driftThresholdRatio: metric.driftThresholdRatio ?? null,
      amendmentThresholdRatio: metric.amendmentThresholdRatio ?? null,
      targetAt: metric.targetAt,
    }));

  return thresholds.length > 0 ? thresholds : undefined;
}

function isReceiptFieldAbsent(
  field: DecisionLedgerReceiptField,
  receipt: Omit<DecisionLedgerReceiptPresentation, 'absentFields'>,
): boolean {
  const value = receipt[field];
  if (Array.isArray(value)) return value.length === 0;
  return value === undefined;
}
